import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { documentLangFor } from "@/lib/documentLanguage";
import {
  LANGUAGE_COOKIE,
  LANGUAGE_COOKIE_MINUTES,
  languageCookieDomain,
  languageCookieOptions,
} from "@/lib/languageCookie";

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    debug: false,

    interpolation: {
      escapeValue: false,
    },

    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      allowMultiLoading: false,
      // Force the browser to revalidate locale files on every load. Without
      // this, a copy cached before a new namespace key was added stays
      // "heuristically fresh" and is served without revalidation, so newly
      // added keys (e.g. errors.licenseExpired) never reach i18next and the
      // raw key string leaks into the UI.
      requestOptions: { cache: "no-cache" },
    },

    detection: {
      // The cookie comes first because it is the choice made anywhere across the
      // Synstream sites: it lives on the parent domain, so switching the
      // language on a dashboard is reflected here, and switching it here reaches
      // every dashboard under the same domain on their next load. localStorage
      // stays behind it as the per-host fallback for when no parent domain is
      // shared, or cookies are blocked. See lib/languageCookie.ts.
      order: ["cookie", "localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "language",
      lookupCookie: LANGUAGE_COOKIE,
      caches: ["localStorage", "cookie"],
      // Without an explicit expiry the detector writes a session cookie, which
      // would lose the choice when the browser closes.
      cookieMinutes: LANGUAGE_COOKIE_MINUTES,
      cookieDomain: languageCookieDomain(),
      cookieOptions: languageCookieOptions(),
      excludeCacheFor: ["cimode"],
      // Normalize detected codes to a locale directory that actually exists
      // under /public/locales (en, zh, zh_CN, jp, ko). Browser navigator codes
      // carry a region (e.g. "en-US", "zh-CN", "ja-JP") that would otherwise
      // request a non-existent folder and fall through to the SPA index.html,
      // leaving every namespace empty and surfacing raw i18n keys in the UI.
      convertDetectedLanguage: (lng: string) => {
        const supported = ["en", "zh", "zh_CN", "jp", "ko"];
        if (supported.includes(lng)) return lng;
        // "zh-CN" -> "zh_CN" (file uses underscore)
        const underscored = lng.replace(/-/g, "_");
        if (supported.includes(underscored)) return underscored;
        // Strip region: "en-US" -> "en", "zh-TW" -> "zh", "ja-JP" -> "ja"
        const base = lng.split(/[-_]/)[0].toLowerCase();
        const baseAlias: Record<string, string> = { ja: "jp" };
        const mapped = baseAlias[base] ?? base;
        return supported.includes(mapped) ? mapped : "en";
      },
    },

    ns: [
      "common",
      // Preloaded because the agreement modal can open on the first render
      // after sign-in, and an empty modal is worse than a slow one.
      "legal",
      "header",
      "sidebar",
      "home",
      "login",
      "passwordReset",
      "settings",
      "pipeline-studio",
      "plans",
      "table",
      "notFound",
    ],
    defaultNS: "common",
  });

// Keep `<html lang>` on the language actually being rendered.
//
// It was left at the index.html default of "en" for the life of the page, so a
// console in Japanese was still a document declaring English: screen readers
// read it in an English voice, and Chrome offered to translate a page that was
// already translated.
//
// The tag is mapped, not the raw app code -- see lib/documentLanguage.ts.
const applyDocumentLanguage = (lng: string) => {
  document.documentElement.lang = documentLangFor(lng);
};

i18n.on("languageChanged", applyDocumentLanguage);
// Once up front as well: the first language is settled during init, which can
// resolve before this handler is attached.
applyDocumentLanguage(i18n.resolvedLanguage ?? i18n.language);

export default i18n;
